/**
 * Repeatable steps: one order carrying several services ("batches").
 *
 * A customer with 3 rolls of C-41 to develop+scan AND 2 rolls of B&W to
 * develop only used to have to fill the whole form twice. A step marked
 * `repeat` can now be answered more than once.
 *
 * The trick is that this is a PURE PRE-PROCESSING step: the definition is
 * expanded into an ordinary flat definition whose extra copies carry prefixed
 * ids, and every existing mechanism (visibility, validation, screen building,
 * wire payload) then works on it untouched. Nothing downstream knows batches
 * exist until buildBatchPayload regroups the answers at the end.
 *
 * The Worker runs this same expansion before validating, so a submission is
 * checked against exactly the definition the customer answered.
 */
import type { FieldItem, FormDefinition, Item, ShowWhen, Step } from './types';

/** Batch 0 keeps original ids and wires, so single-service orders are byte-identical to before. */
export const BATCH_PREFIX = 'b';

export const batchFieldId = (batch: number, id: string) =>
  batch === 0 ? id : `${BATCH_PREFIX}${batch}_${id}`;

/** The gate question closing each batch ("add another service?"). */
export const addMoreId = (batch: number) => `__addMore${batch}`;
export const ADD_MORE_YES = 'Add another service';
export const ADD_MORE_NO = "No, that's everything";

/** The step a definition repeats, or null when it has none. */
export function repeatStep(def: FormDefinition): Step | null {
  return def.steps.find((s) => s.repeat) ?? null;
}

export function maxBatches(def: FormDefinition): number {
  const step = repeatStep(def);
  return Math.max(1, step?.repeat?.max ?? 1);
}

function remapRule(when: ShowWhen | undefined, batch: number): ShowWhen | undefined {
  if (!when) return undefined;
  // Rules only ever reference fields in their own batch, so remapping the
  // field id is enough to keep each copy independent of the others.
  return { mode: when.mode, rules: when.rules.map((r) => ({ ...r, field: batchFieldId(batch, r.field) })) };
}

function remapItem(item: Item, batch: number): Item {
  const id = batchFieldId(batch, item.id);
  const showWhen = remapRule(item.showWhen, batch);
  if (item.kind === 'section') {
    return { ...item, id, showWhen, items: item.items.map((c) => remapItem(c, batch)) };
  }
  if (item.kind === 'field') {
    // The wire keeps a batch prefix too; buildBatchPayload strips it back off
    // when grouping, and batch 0 has no prefix at all.
    return {
      ...item,
      id,
      showWhen,
      // Option-level rules need remapping too, or batch 2's Discard option
      // would key on batch 1's service.
      options: item.options?.map((o) =>
        (o.showWhen ? { ...o, showWhen: remapRule(o.showWhen, batch) } : o)),
      wire: batch === 0 ? item.wire : `${BATCH_PREFIX}${batch}:${item.wire}`,
    };
  }
  return { ...item, id, showWhen };
}

function addMoreField(batch: number, itemLabel: string): FieldItem {
  return {
    kind: 'field',
    id: addMoreId(batch),
    wire: `__addMore${batch}`,
    type: 'radio',
    label: `Do you have film needing a different ${itemLabel}?`,
    help: 'Different services are processed separately, so each one gets its own job bag.',
    required: true,
    options: [{ value: ADD_MORE_YES }, { value: ADD_MORE_NO }],
  };
}

/**
 * Expand `def` to `count` copies of its repeatable step. `count` is clamped to
 * [1, repeat.max]. A definition with no repeatable step is returned unchanged,
 * so this is safe to call on every form.
 */
export function expandRepeats(def: FormDefinition, count: number): FormDefinition {
  const step = repeatStep(def);
  if (!step) return def;
  const max = maxBatches(def);
  const batches = Math.min(Math.max(1, Math.floor(count) || 1), max);
  const itemLabel = step.repeat?.itemLabel ?? 'service';

  const steps: Step[] = [];
  for (const s of def.steps) {
    if (s !== step) {
      steps.push(s);
      continue;
    }
    for (let b = 0; b < batches; b += 1) {
      const items = s.items.map((it) => remapItem(it, b));
      // Every batch but the last possible one ends with the gate question.
      if (b < max - 1) items.push(addMoreField(b, itemLabel));
      steps.push({ ...s, id: batchFieldId(b, s.id), items });
    }
  }
  return { ...def, steps };
}

/**
 * How many batches the answers describe: 1, plus one for each consecutive
 * gate answered "yes". Derived rather than stored so the Worker reaches the
 * same number from the payload alone.
 */
export function batchCountFromAnswers(
  def: FormDefinition,
  answers: Record<string, unknown>,
): number {
  const max = maxBatches(def);
  let count = 1;
  while (count < max && answers[addMoreId(count - 1)] === ADD_MORE_YES) count += 1;
  return count;
}

/**
 * Regroup a flat wire payload into per-batch objects.
 *
 * Batch 0's wires are left flat exactly as they always were, so every existing
 * consumer (the Square draft, the admin viewer, the single-service job bag)
 * keeps working with no changes at all. `Batches` carries every batch in order
 * including the first, which is what the multi-page job bag reads.
 */
export function buildBatchPayload(
  flat: Record<string, string | string[]>,
  batches: number,
): Record<string, unknown> {
  const out: Record<string, string | string[]> = {};
  const grouped: Array<Record<string, string | string[]>> = Array.from(
    { length: batches },
    () => ({}),
  );

  for (const [wire, value] of Object.entries(flat)) {
    const m = /^b(\d+):(.+)$/.exec(wire);
    if (m) {
      const idx = Number(m[1]);
      if (idx > 0 && idx < batches) grouped[idx][m[2]] = value;
      continue;
    }
    if (/^__addMore\d+$/.test(wire)) continue; // internal plumbing, not an answer
    out[wire] = value;
    grouped[0][wire] = value;
  }

  const result: Record<string, unknown> = { ...out };
  if (batches > 1) result.Batches = grouped;
  return result;
}
